import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { FaAngleLeft, FaAngleRight, FaEdit } from "react-icons/fa";
import { PAGE_SIZE } from "../Utils/constant";
import TextInput from "../common/TextInput";
import { supabase } from "../../supabaseClient";

const VehicleLogsNew = () => {
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [log, setLog] = useState(null);
  const [search, setSearch] = useState(null);
  const [logsData, setLogsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const [logTypes, setLogTypes] = useState([]);
  const [users, setUsers] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      VehicleID: "",
      VehicleLogTypeID: "",
      UserID: "",
      LogDate: "",
      Mileage: "",
      odometer_recorded: false,
      tire_condition_good: false,
      no_vehicle_damage: false,
      no_dashboard_lights: false,
      safety_kit_present: false,
      inspection_notes: "",
      submitted_by_driver: false,
    },
  });

  useEffect(() => {
    if (log) {
      reset({
        VehicleID: log.VehicleID,
        VehicleLogTypeID: log.VehicleLogTypeID,
        UserID: log.UserID,
        LogDate: log.LogDate ? log.LogDate.split("T")[0] : "",
        Mileage: log.Mileage,
        odometer_recorded: log.odometer_recorded || false,
        tire_condition_good: log.tire_condition_good || false,
        no_vehicle_damage: log.no_vehicle_damage || false,
        no_dashboard_lights: log.no_dashboard_lights || false,
        safety_kit_present: log.safety_kit_present || false,
        inspection_notes: log.inspection_notes || "",
        submitted_by_driver: log.submitted_by_driver || false,
      });
    }
  }, [log, reset]);

  useEffect(() => {
    const fetchLookups = async () => {
      const { data: Vehicles } = await supabase
        .from("Vehicles")
        .select("VehicleID, Code, Model");
      setVehicles(Vehicles || []);
      const { data: LogTypes } = await supabase
        .from("VehicleLogTypes")
        .select("VehicleLogTypeID, Description");
      setLogTypes(LogTypes || []);
      const { data: Users } = await supabase
        .from("Users")
        .select("UserID, Email");
      setUsers(Users || []);
    };
    fetchLookups();
  }, []);

  useEffect(() => {
    const fetchLogs = async () => {
      let query = supabase
        .from("VehicleLogs")
        .select(
          "VehicleLogID, VehicleID, VehicleLogTypeID, UserID, LogDate, Mileage, odometer_recorded, tire_condition_good, no_vehicle_damage, no_dashboard_lights, safety_kit_present, inspection_notes, submitted_by_driver",
          { count: "exact", head: false }
        )
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);
      if (search && search.text) {
        query = query.ilike(search.type, `%${search.text}%`);
      }
      const { data, count, error } = await query;
      if (!error) {
        setLogsData(data);
        setTotalCount(count);
      }
    };
    fetchLogs();
  }, [page, search, isEdit]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  const onPrevPage = useCallback(() => {
    setPage((old) => Math.max(old - 1, 1));
  }, []);

  const onSuccess = useCallback(() => {
    toast.success("Success");
    setIsEdit(false);
    setLog(null);
  }, []);
  const onError = useCallback((err) => {
    toast.error("Error: " + err.message);
  }, []);

  const onAddNew = () => {
    reset({
      VehicleID: vehicles[0] ? vehicles[0].VehicleID : "",
      VehicleLogTypeID: logTypes[0] ? logTypes[0].VehicleLogTypeID : "",
      UserID: users[0] ? users[0].UserID : "",
      LogDate: "",
      Mileage: "",
      odometer_recorded: false,
      tire_condition_good: false,
      no_vehicle_damage: false,
      no_dashboard_lights: false,
      safety_kit_present: false,
      inspection_notes: "",
      submitted_by_driver: false,
    });
    setIsEdit(true);
    setLog(null);
  };

  const OnCancel = () => {
    setIsEdit(false);
    setLog(null);
  };

  const OnCreate = useCallback(
    async (data) => {
      if (log) {
        // Update
        const { error } = await supabase
          .from("VehicleLogs")
          .update({
            VehicleID: data.VehicleID,
            VehicleLogTypeID: data.VehicleLogTypeID,
            UserID: data.UserID,
            LogDate: data.LogDate,
            Mileage: data.Mileage,
            odometer_recorded: data.odometer_recorded,
            tire_condition_good: data.tire_condition_good,
            no_vehicle_damage: data.no_vehicle_damage,
            no_dashboard_lights: data.no_dashboard_lights,
            safety_kit_present: data.safety_kit_present,
            inspection_notes: data.inspection_notes,
            submitted_by_driver: data.submitted_by_driver,
          })
          .eq("VehicleLogID", log.VehicleLogID);
        if (error) onError(error);
        else onSuccess();
      } else {
        // Create
        const { error } = await supabase.from("VehicleLogs").insert({
          VehicleID: data.VehicleID,
          VehicleLogTypeID: data.VehicleLogTypeID,
          UserID: data.UserID,
          LogDate: data.LogDate,
          Mileage: data.Mileage,
          odometer_recorded: data.odometer_recorded,
          tire_condition_good: data.tire_condition_good,
          no_vehicle_damage: data.no_vehicle_damage,
          no_dashboard_lights: data.no_dashboard_lights,
          safety_kit_present: data.safety_kit_present,
          inspection_notes: data.inspection_notes,
          submitted_by_driver: data.submitted_by_driver,
        });
        if (error) onError(error);
        else onSuccess();
      }
    },
    [log, onError, onSuccess]
  );

  const OnSearch = useCallback((data) => {
    setSearch(data);
  }, []);

  return (
    <>
      {isEdit ? (
        <div className="driver-patent-edit-content mt-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <div>Vehicle</div>
            <div className="w-35">
              <Controller
                control={control}
                name="VehicleID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.VehicleID?.message}
                  >
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.VehicleID} value={vehicle.VehicleID}>
                        {vehicle.Code} {vehicle.Model}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Log Type</div>
            <div className="w-35">
              <Controller
                control={control}
                name="VehicleLogTypeID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.VehicleLogTypeID?.message}
                  >
                    {logTypes.map((type) => (
                      <option
                        key={type.VehicleLogTypeID}
                        value={type.VehicleLogTypeID}
                      >
                        {type.Description}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>User</div>
            <div className="w-35">
              <Controller
                control={control}
                name="UserID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.UserID?.message}
                  >
                    {users.map((user) => (
                      <option key={user.UserID} value={user.UserID}>
                        {user.Email}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Log Date</div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="LogDate"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <input
                    type="date"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>Mileage</div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="Mileage"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="number"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.Mileage?.message}
                  />
                )}
              />
            </div>
          </div>
          
          {/* Vehicle Inspection Section */}
          <div className="mt-6 border-t pt-4">
            <h6 className="mb-4 font-semibold text-gray-700">Vehicle Inspection Checklist</h6>
            
            <div className="flex justify-between mb-2">
              <div className="flex items-center">
                <span className="text-red-500 mr-1">*</span>
                <span>Odometer reading recorded</span>
              </div>
              <div className="w-35">
                <Controller
                  control={control}
                  name="odometer_recorded"
                  render={({ field: { value, onChange } }) => (
                    <input
                      type="checkbox"
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                      className="w-5 h-5"
                    />
                  )}
                />
              </div>
            </div>
            
            <div className="flex justify-between mb-2">
              <div className="flex items-center">
                <span className="text-red-500 mr-1">*</span>
                <span>No bald tires or excessive wear</span>
              </div>
              <div className="w-35">
                <Controller
                  control={control}
                  name="tire_condition_good"
                  render={({ field: { value, onChange } }) => (
                    <input
                      type="checkbox"
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                      className="w-5 h-5"
                    />
                  )}
                />
              </div>
            </div>
            
            <div className="flex justify-between mb-2">
              <div className="flex items-center">
                <span className="text-red-500 mr-1">*</span>
                <span>No visible damage to vehicle</span>
              </div>
              <div className="w-35">
                <Controller
                  control={control}
                  name="no_vehicle_damage"
                  render={({ field: { value, onChange } }) => (
                    <input
                      type="checkbox"
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                      className="w-5 h-5"
                    />
                  )}
                />
              </div>
            </div>
            
            <div className="flex justify-between mb-2">
              <div className="flex items-center">
                <span className="text-red-500 mr-1">*</span>
                <span>No warning lights on dashboard</span>
              </div>
              <div className="w-35">
                <Controller
                  control={control}
                  name="no_dashboard_lights"
                  render={({ field: { value, onChange } }) => (
                    <input
                      type="checkbox"
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                      className="w-5 h-5"
                    />
                  )}
                />
              </div>
            </div>
            
            <div className="flex justify-between mb-2">
              <div className="flex items-center">
                <span className="text-red-500 mr-1">*</span>
                <span>Safety/fire kit present in vehicle</span>
              </div>
              <div className="w-35">
                <Controller
                  control={control}
                  name="safety_kit_present"
                  render={({ field: { value, onChange } }) => (
                    <input
                      type="checkbox"
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                      className="w-5 h-5"
                    />
                  )}
                />
              </div>
            </div>
            
            <div className="flex justify-between mb-2">
              <div>Inspection Notes</div>
              <div className="flex justify-between w-35">
                <Controller
                  control={control}
                  name="inspection_notes"
                  render={({ field: { value, onChange } }) => (
                    <textarea
                      value={value}
                      onChange={onChange}
                      placeholder="Additional inspection notes..."
                      className="custom-input rounded-lg w-100"
                      rows="3"
                    />
                  )}
                />
              </div>
            </div>
            
            <div className="flex justify-between mb-2">
              <div>Submitted by Driver</div>
              <div className="w-35">
                <Controller
                  control={control}
                  name="submitted_by_driver"
                  render={({ field: { value, onChange } }) => (
                    <input
                      type="checkbox"
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                      className="w-5 h-5"
                    />
                  )}
                />
              </div>
            </div>
            
            <div className="text-sm text-gray-600 mt-2">
              <span className="text-red-500">*</span> Critical items - all must be checked for safe operation
            </div>
          </div>
          
          <div className="flex gap-4 mt-4 save-button">
            <button
              disabled={false}
              onClick={handleSubmit(OnCreate)}
              className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              <span>{log ? "Update" : "Create"}</span>
            </button>
            <div>
              <div
                onClick={OnCancel}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex ">
            <h5 className="mb-0">Vehicle Logs</h5>
            <button
              className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
              onClick={onAddNew}
            >
              <span>Add New</span>
            </button>
          </div>
          <SearchBar setSearch={setSearch} OnSearch={OnSearch} />
          <table className="table-auto w-full mt-4 border border-gray-200">
            <thead>
              <tr className="bg-gray-100 cursor-pointer">
                <th className="px-4 py-2 border">ID</th>
                <th className="px-4 py-2 border">Vehicle</th>
                <th className="px-4 py-2 border">Log Type</th>
                <th className="px-4 py-2 border">User</th>
                <th className="px-4 py-2 border">Date</th>
                <th className="px-4 py-2 border">Mileage</th>
                <th className="px-4 py-2 border">Odometer</th>
                <th className="px-4 py-2 border">Tires</th>
                <th className="px-4 py-2 border">Damage</th>
                <th className="px-4 py-2 border">Dash</th>
                <th className="px-4 py-2 border">Safety Kit</th>
                <th className="px-4 py-2 border">Notes</th>
                <th className="px-4 py-2 border">Status</th>
                <th className="px-4 py-2 border">Driver</th>
                <th className="px-4 py-2 border">Edit</th>
              </tr>
            </thead>
            <tbody>
              {logsData &&
                logsData.map((log) => (
                  <tr key={log.VehicleLogID} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border">{log.VehicleLogID}</td>
                    <td className="px-4 py-2 border">
                      {vehicles.find((v) => v.VehicleID === log.VehicleID)
                        ? `${
                            vehicles.find((v) => v.VehicleID === log.VehicleID)
                              .Code
                          } ${
                            vehicles.find((v) => v.VehicleID === log.VehicleID)
                              .Model
                          }`
                        : ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {logTypes.find(
                        (t) => t.VehicleLogTypeID === log.VehicleLogTypeID
                      )?.Description || ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {users.find((u) => u.UserID === log.UserID)?.Email || ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {log.LogDate ? log.LogDate.split("T")[0] : ""}
                    </td>
                    <td className="px-4 py-2 border">{log.Mileage}</td>
                    <td className="px-4 py-2 border text-center">
                      {log.odometer_recorded ? (
                        <span className="text-green-600">✓</span>
                      ) : (
                        <span className="text-red-600">✗</span>
                      )}
                    </td>
                    <td className="px-4 py-2 border text-center">
                      {log.tire_condition_good ? (
                        <span className="text-green-600">✓</span>
                      ) : (
                        <span className="text-red-600">✗</span>
                      )}
                    </td>
                    <td className="px-4 py-2 border text-center">
                      {log.no_vehicle_damage ? (
                        <span className="text-green-600">✓</span>
                      ) : (
                        <span className="text-red-600">✗</span>
                      )}
                    </td>
                    <td className="px-4 py-2 border text-center">
                      {log.no_dashboard_lights ? (
                        <span className="text-green-600">✓</span>
                      ) : (
                        <span className="text-red-600">✗</span>
                      )}
                    </td>
                    <td className="px-4 py-2 border text-center">
                      {log.safety_kit_present ? (
                        <span className="text-green-600">✓</span>
                      ) : (
                        <span className="text-red-600">✗</span>
                      )}
                    </td>
                    <td className="px-4 py-2 border">
                      {log.inspection_notes ? (
                        <span className="truncate block max-w-[150px]" title={log.inspection_notes}>
                          {log.inspection_notes}
                        </span>
                      ) : "-"}
                    </td>
                    <td className="px-4 py-2 border">
                      {log.odometer_recorded && log.tire_condition_good && log.no_vehicle_damage && log.no_dashboard_lights && log.safety_kit_present ? (
                        <span className="text-green-600 font-semibold">PASS</span>
                      ) : (
                        <span className="text-red-600 font-semibold">FAIL</span>
                      )}
                    </td>
                    <td className="px-4 py-2 border text-center">
                      {log.submitted_by_driver ? (
                        <span className="text-blue-600">✓</span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2 border cursor-point text-center">
                      <FaEdit
                        onClick={() => {
                          setLog(log);
                          setIsEdit(true);
                        }}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="pagination-container">
            <div className="pagination-content">
              <div>
                <p className="pagination-text">
                  Showing <span>{(page - 1) * PAGE_SIZE + 1}</span> to{" "}
                  <span>{Math.min(totalCount, page * PAGE_SIZE)}</span> of{" "}
                  <span>{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="pagination-nav">
                  <button
                    onClick={onPrevPage}
                    disabled={page == 1}
                    className={`pagination-item ${page == 1 ? "disabled" : ""}`}
                  >
                    <FaAngleLeft className="pagination-icon" />
                  </button>
                  <button
                    onClick={onNextPage}
                    disabled={page * PAGE_SIZE > totalCount}
                    className={`pagination-item ${
                      page * PAGE_SIZE > totalCount ? "disabled" : ""
                    }`}
                  >
                    <FaAngleRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VehicleLogsNew;

const SearchBar = ({ setSearch, OnSearch }) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: {
      type: "Mileage",
      text: "",
    },
  });

  return (
    <div className="flex mt-4 items-center">
      <div>
        <p className="mb-0">Look For:</p>
      </div>
      <div className="flex justify-between w-10 ml-1">
        <Controller
          control={searchControl}
          name="type"
          render={({ field: { value, onChange } }) => (
            <select
              className="custom-input rounded-lg cursor-pointer"
              value={value}
              onChange={onChange}
            >
              <option value={"Mileage"}>Mileage</option>
              <option value={"LogDate"}>Log Date</option>
            </select>
          )}
        />
      </div>
      <div className="ml-1">
        <Controller
          control={searchControl}
          name="text"
          rules={{ required: "Required" }}
          render={({ field: { value, onChange } }) => (
            <input
              type="text"
              value={value}
              onChange={onChange}
              className="custom-input rounded-lg w-100 "
            />
          )}
        />
      </div>
      <div className="ml-1 flex">
        <div>
          <button
            onClick={handleSearchSubmit(OnSearch)}
            className="justify-between rounded-lg flex items-center w-30 custom-search-button border-none"
          >
            <span>Search</span>
          </button>
        </div>
        <div>
          <button
            onClick={() => {
              searchReset({ type: "Mileage", text: "" });
              setSearch(null);
            }}
            className="justify-between rounded-lg ml-1 flex items-center custom-reset-button"
          >
            <span>Reset</span>
          </button>
        </div>
      </div>
    </div>
  );
};
